// FLAG:useDocuAi — pure helpers for turning an upload failure into something a person can act on.
// Leaf module (no store, no axios, no lang import) so both halves stay unit-testable in isolation.
//
// Two separate problems, one per export:
//
//  1. `enrichUploadError` — onUploadError only ever destructures `{ response }`, so a failure with
//     NO http response at all (connection reset, DNS, our own timeout firing) loses everything: the
//     axios `code` and `message` are dropped and the row is left with the generic
//     "Datei konnte nicht hochgeladen werden." That is exactly the class of failure a mass upload
//     hits, and it is the reason a post-mortem on a failed batch cannot say what happened. This
//     puts the technical detail back into the message BEFORE onUploadError sees it, without
//     touching onUploadError itself (shared with the legacy path).
//
//  2. `getUploadErrorText` — the flyout rendered the CONSTANT `states.error` for every failure and
//     dropped the message the row was carrying, so even a precise server answer ("Dieser Dateityp
//     ist nicht erlaubt", a quota refusal) reached the user as an unqualified "Fehler beim
//     Hochladen".

//  3. `isMissingUploadError` — cancel-upload answers `ValidationError({ document: NOT_FOUND })`
//     when no File row matches the token. That is what the ✕ hits once the user has already
//     deleted the document from the grid, and because onCancel only removes the row inside `.then`
//     the row then became unremovable: the upload it refers to is gone, but the flyout kept it (and
//     with it the "n of m complete" counter) until a page reload.

import { errorCodes } from '@/modules/base/util/constants'

export type AxiosLikeError = {
  code?: string,
  message?: string,
  response?: {
    status?: number,
    data?: {
      message?: string,
      meta?: Record<string, unknown>,
      [key: string]: unknown,
    },
  },
}

const isUsableText = (value: unknown): value is string =>
  typeof value === 'string' && value.trim().length > 0

/**
 * A short technical description of a failure that carries no server message: the http status when
 * there is a response, otherwise the axios error code and/or message. Empty string when the error
 * says nothing useful at all.
 */
export const describeUploadFailure = (error: AxiosLikeError): string => {
  const status = error?.response?.status
  if (typeof status === 'number') {
    return `HTTP ${status}`
  }
  const parts = [error?.code, error?.message].filter(isUsableText)
  return parts.join(': ')
}

/**
 * Ensure the error carries a `response.data.message` that names the actual reason, so the message
 * onUploadError stores on the pending row is worth reading.
 *
 * Returns the error UNCHANGED when it already has a server message — a real API answer always wins
 * over anything we could synthesise. The clone is shallow and only ever adds `response.data.message`;
 * `response.data.meta` is preserved, which matters because onUploadError branches on it (quota
 * refusals, rejected file types) and must keep doing so.
 */
export const enrichUploadError = (error: AxiosLikeError, fallbackMessage: string): AxiosLikeError => {
  if (isUsableText(error?.response?.data?.message)) {
    return error
  }

  const detail = describeUploadFailure(error)
  const message = detail ? `${fallbackMessage} (${detail})` : fallbackMessage

  return {
    ...error,
    response: {
      ...error?.response,
      data: {
        ...error?.response?.data,
        message,
      },
    },
  }
}

/**
 * Whether the server is telling us this upload does not exist any more, so the row is a ghost and
 * removing it locally is the correct answer rather than a swallowed failure.
 *
 * `ValidationError({ document: NOT_FOUND })` serialises its meta as `{ document: [{ code }] }` (see
 * the ValidationError constructor in api/src/errors.ts), the same shape onUploadError already reads
 * for rejected mimetypes. A plain 404 is accepted too, so this keeps working if the route ever
 * answers with a status instead of a meta code.
 */
export const isMissingUploadError = (error: AxiosLikeError): boolean => {
  const documentMeta = error?.response?.data?.meta?.document
  if (Array.isArray(documentMeta)
    && documentMeta.some((item) => (item as { code?: string })?.code === errorCodes.NOT_FOUND)) {
    return true
  }
  return error?.response?.status === 404
}

/**
 * The text to show on a failed row. Falls back to the generic label whenever the row carries
 * nothing readable — a row restored by fetchPendingUploads can hold an object rather than a string.
 */
export const getUploadErrorText = (error: unknown, fallback: string): string => {
  if (isUsableText(error)) {
    return error
  }
  const message = (error as { message?: unknown })?.message
  if (isUsableText(message)) {
    return message
  }
  return fallback
}
